import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import "../promotions/promotions.css";
import "./audits.css";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";

/*
	Auditoría (Finanzas, sección 92) — Etapa 4 de cobros (plan v7: decisiones 4 y 11; escalar = Martha Cruz y Helen).
	Lo que los cobros mandan a revisión, en tres colas: Pagos a Auditar (referencia BAC repetida, pago anulado), Correcciones a
	Auditar (recargo eximido o anulado por comprobante a tiempo) y Descuentos a Auditar (descuento nuevo para el alumno, excepción
	aprobada, descuento especial, acuerdos anulados o perdidos, el 50 % que cae, grupos por resolver). Contabilidad certifica:
	Certificado · Con observación · Escalado (Cliq a Martha Cruz y Helen, que lo cierran).
	Preguntar (ESPEC_AUDITORIA_PREGUNTAS C): en cualquier caso, «Preguntar» → «Con pregunta»; le llega por Cliq a quien aplicó con su
	supervisión en copia; cuando responde vuelve como «Respondido» y se certifica, se observa o se escala como siempre. La
	conversación completa queda en el caso. Enlace directo: /audits?caso=ID&plantel=N (lo que manda el Cliq).
	Teléfono: una columna, botones de 44 px, nada que dependa del hover, el botón se apaga mientras se envía (doble toque).
	Todo lo valida el servidor.
*/

const http = HttpManager.getInstance();
const STATUS_CLASS: Record<number, string> = { 1: "wdChipOro", 2: "wdChipAcento", 3: "wdChipOro", 4: "wdChipOro", 5: "wdChipFilo", 6: "wdChipOro" };
const TABS: { key: string; label: string }[] = [
	{ key: "pagos", label: "Pagos a Auditar" }, { key: "correcciones", label: "Correcciones a Auditar" },
	{ key: "descuentos", label: "Descuentos a Auditar" }, { key: "ventas", label: "Ventas a Terceros" }, { key: "escalados", label: "Escalados" }
];
const fechaHora = fechaHoraAmPm;   // siempre am/pm

/** Preguntas y respuestas del caso, como conversación (lo de revisión va aparte). */
export const Conversacion: React.FC<{ history: any[] }> = ({ history }) => {
	const conv = (history || []).filter((h: any) => h.kind === "pregunta" || h.kind === "respuesta");
	if (!conv.length) return null;
	return (
		<div className="auConversacion">
			{conv.map((h: any, i: number) => (
				<div key={"c" + i} className={"auMensaje " + (h.kind === "pregunta" ? "auPregunta" : "auRespuesta")}>
					<div className="auMensajeQuien">{h.kind === "pregunta" ? "Pregunta de Auditoría" : "Respuesta"} · {h.by} · {fechaHora(h.at)}</div>
					<div className="auMensajeTexto">{h.notes}</div>
				</div>
			))}
		</div>
	);
};

const AuditsController: React.FC = () => {
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;
	const campusName = legacyCampus?.name || sessionCampus?.name || "";

	const [tab, setTab] = useState<string>("pagos");
	const [status, setStatus] = useState<string>("pendientes");
	const [q, setQ] = useState<string>("");
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [abierto, setAbierto] = useState<any>(null);
	const [nota, setNota] = useState<string>("");
	const [pregunta, setPregunta] = useState<string>("");
	const [modo, setModo] = useState<"revisar" | "preguntar">("revisar");
	const [busy, setBusy] = useState<boolean>(false);
	const enviando = useRef<boolean>(false);   // doble toque: un solo envío a la vez, aunque el estado no alcance a pintarse
	const arriba = useRef<HTMLDivElement | null>(null);

	const load = () => {
		if (!campusId) return;
		http.paymentService.audits(campusId, { category: tab, status, q }).then((r: any) => { setData(r.data); setError(null); })
			.catch((e: any) => setError(serverError(e)));
	};
	useEffect(() => {
		setData(null);
		const t = setTimeout(load, q ? 300 : 0);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, tab, status, q]);

	const abrir = async (id: number, forzar = false) => {
		if (!forzar && abierto && abierto.id === id) { setAbierto(null); return; }
		try {
			const r = await http.paymentService.audit(campusId!, id);
			setAbierto(r.data); setNota(""); setPregunta(""); setModo("revisar");
			if (r.data.category && r.data.category !== tab && forzar) { setTab(r.data.statusId === 4 ? "escalados" : r.data.category); }
		} catch (e) { setError(serverError(e)); }
	};
	// Enlace del Cliq: /audits?caso=ID&plantel=N → cambia de plantel si hace falta y abre ese caso.
	const enlace = useEnlaceDirecto("caso", (id) => { abrir(id, true).then(() => setTimeout(() => (document.querySelector(".auDetalleCaso") || arriba.current)?.scrollIntoView({ block: "start" }), 250)); });

	const enviar = async (fn: () => Promise<any>) => {
		if (enviando.current) return;
		enviando.current = true;
		setBusy(true);
		try {
			const r = await fn();
			setAbierto(r.data); setNota(""); setPregunta(""); load();
		} catch (e) {
			setError(serverError(e));
		} finally {
			enviando.current = false;
			setBusy(false);
		}
	};
	const revisar = (statusId: number) => {
		if (!abierto || !campusId) return;
		if (statusId !== 2 && nota.trim().length < 10) { setError("Escribe la observación o por qué se escala (al menos 10 caracteres)."); return; }
		if (statusId === 4 && !window.confirm("¿Escalar a " + (data?.escalateTo || []).join(" y ") + "?")) return;
		enviar(() => http.paymentService.reviewAudit(campusId, abierto.id, statusId, nota.trim()));
	};
	const preguntar = () => {
		if (!abierto || !campusId) return;
		if (pregunta.trim().length < 10) { setError("Escribe la pregunta (al menos 10 caracteres)."); return; }
		enviar(() => http.paymentService.askAudit(campusId, abierto.id, pregunta.trim()));
	};

	if (!campusId) return <div className="container-fluid waModulo waDescuentos" style={{ padding: 24 }}>Elige un plantel.</div>;
	const counts = data?.counts || {};
	const puedeActuar = (a: any) => a && a.statusId !== 2 && a.canReview && a.canAsk !== false;

	const detalle = (a: any) => (
		<div className="wdDetalle auDetalleCaso">
			<div className="auDetalleCabeza"><b>{a.reasonName}</b> · caso #{a.id} <span className={"wdChip " + (STATUS_CLASS[a.statusId] || "wdChipFilo")}>{a.status}</span>{a.escalated && a.statusId !== 4 ? <span className="wdChip wdChipOro">Escalado</span> : null}</div>
			{a.student ? <div className="wdTenue" style={{ fontSize: 14 }}>{a.student.name}{a.student.identityCode ? " · " + a.student.identityCode : ""}</div> : null}
			<div className="auTexto">{a.details}</div>
			<div className="wdTenue" style={{ fontSize: 13 }}>
				{a.createdBy ? "Aplicó / abrió: " + a.createdBy : ""}{a.approvedBy ? " · Aprobó: " + a.approvedBy : ""}{a.screen ? " · " + a.screen : ""}
			</div>
			{a.reviewedBy ? <div className="wdTenue" style={{ fontSize: 13 }}>
				{a.statusId === 2 ? "Revisado y certificado por " : "Revisó "}{a.reviewedBy} · {fechaHora(a.reviewedAt)}{a.reviewNotes ? " · " + a.reviewNotes : ""}
			</div> : null}
			{(a.participants || []).length ? (
				<div className="wdTenue" style={{ fontSize: 13 }}>En la conversación: {a.participants.map((p: any) => p.name + " (" + p.roleName + ")").join(", ")}</div>
			) : null}
			<Conversacion history={a.history || []} />
			{(a.history || []).filter((h: any) => h.kind !== "pregunta" && h.kind !== "respuesta").length ? (
				<div style={{ marginTop: 6 }}>{a.history.filter((h: any) => h.kind !== "pregunta" && h.kind !== "respuesta").map((h: any, i: number) => (
					<div key={"h" + i} className="wdEvento"><span className="wdTenue">{fechaHora(h.at)} · {h.by}</span> · {h.from} → <b>{h.to}</b>{h.notes ? " · " + h.notes : ""}</div>
				))}</div>
			) : null}
			{a.statusId === 5 ? <div className="wdAviso" style={{ marginTop: 8 }}>Esperando respuesta{a.askedAt ? " desde el " + fechaHora(a.askedAt) : ""}</div> : null}
			{puedeActuar(a) ? (
				<div className="auAcciones">
					<div className="auModos" role="tablist">
						<button type="button" className={"auModo" + (modo === "revisar" ? " auModoOn" : "")} onClick={() => setModo("revisar")}>Revisar</button>
						<button type="button" className={"auModo" + (modo === "preguntar" ? " auModoOn" : "")} onClick={() => setModo("preguntar")}>Preguntar</button>
					</div>
					{modo === "revisar" ? (
						<>
							<textarea className="form-control auCaja" rows={2} placeholder="Nota (obligatoria para observación o escalado)" value={nota} disabled={busy} onChange={(e) => setNota(e.target.value)} />
							<div className="auBotones">
								<button type="button" className="wdBoton wdPrimario" disabled={busy} onClick={() => revisar(2)}>{busy ? "Enviando…" : "Certificado"}</button>
								{a.statusId !== 3 ? <button type="button" className="wdBoton" disabled={busy || nota.trim().length < 10} onClick={() => revisar(3)}>Con Observación</button> : null}
								{a.statusId !== 4 ? <button type="button" className="wdBoton" disabled={busy || nota.trim().length < 10} onClick={() => revisar(4)}>Escalar</button> : null}
							</div>
						</>
					) : (
						<>
														<textarea className="form-control auCaja" rows={3} placeholder="Tu pregunta (al menos 10 caracteres)" value={pregunta} disabled={busy} onChange={(e) => setPregunta(e.target.value)} />
							<div className="auBotones">
								<button type="button" className="wdBoton wdPrimario" disabled={busy || pregunta.trim().length < 10} onClick={preguntar}>{busy ? "Enviando…" : "Preguntar"}</button>
							</div>
						</>
					)}
				</div>
			) : a.isParty ? <div className="wdAviso" style={{ fontSize: 13 }}>Participas en este caso (lo aplicaste, lo aprobaste o te preguntaron): lo audita otra persona de Contabilidad o Contraloría.</div>
				: (a.escalated || a.statusId === 4) && !a.canCloseEscalated ? <div className="wdTenue" style={{ fontSize: 13 }}>Escalado: lo cierran {(data?.escalateTo || []).join(" o ")}.</div> : null}
		</div>
	);

	const enLista = abierto && data && data.data.some((a: any) => a.id === abierto.id);

	return (
		<div ref={arriba} className="container-fluid waModulo waDescuentos waAuditoria" style={{ padding: movil ? "10px 12px 24px 12px" : "10px 24px 32px 24px" }}>
			<div className="wdCabeza">
				<h4 className="wdTitulo">Auditoría</h4>
				<span className="wdTenue">{campusName}</span>
			</div>
			<div className="wdTabs">
				{TABS.map((t) => (
					<button key={t.key} type="button" className={"wdTab" + (tab === t.key ? " wdTabOn" : "")} onClick={() => { setTab(t.key); setAbierto(null); }}>
						{t.label}{counts[t.key] ? <span className="wdChip wdChipOro" style={{ marginLeft: 6 }}>{counts[t.key]}</span> : null}
					</button>
				))}
			</div>
			{enlace.aviso ? <div className="wdAviso" role="alert" onClick={enlace.cerrar}>{enlace.aviso}</div> : null}
			{error ? <div className="wdAviso" onClick={() => setError(null)}>{error}</div> : null}
			{counts.respondidos || counts.conPregunta ? (
				<div className="wdTenue" style={{ fontSize: 13, marginBottom: 6 }}>
					{counts.respondidos ? counts.respondidos + " respondido" + (counts.respondidos === 1 ? "" : "s") + " por revisar" : ""}
					{counts.respondidos && counts.conPregunta ? " · " : ""}
					{counts.conPregunta ? counts.conPregunta + " esperando respuesta" : ""}
				</div>
			) : null}
			{data && !data.canReview ? <div className="wdTenue" style={{ fontSize: 13, margin: "6px 0" }}>Solo Lectura</div> : null}
			{tab !== "escalados" ? (
				<div className="wdFiltros">
					<select className="form-select form-select-sm auFiltro" value={status} onChange={(e) => setStatus(e.target.value)}>
						<option value="pendientes" title="Por auditar, respondidos, con pregunta y con observación (lo escalado va en Escalados)">Pendientes</option>
						<option value="1">Por Auditar</option><option value="6">Respondidos</option><option value="5">Con Pregunta</option>
						<option value="3">Con Observación</option><option value="4">Escalados</option>
						<option value="2">Certificados</option><option value="">Todos</option>
					</select>
					<input className="form-control form-control-sm auFiltro" placeholder="Buscar en el detalle o número de pago" value={q} onChange={(e) => setQ(e.target.value)} />
				</div>
			) : null}
			{abierto && data && !enLista ? detalle(abierto) : null}
			{data === null ? <div className="loader" /> : !data.data.length ? <div className="wdTenue">Nada aquí.</div> : data.data.map((a: any) => (
				<React.Fragment key={"a" + a.id}>
					<button type="button" className="auFila" onClick={() => abrir(a.id)}>
						<div><span className={"wdChip " + (STATUS_CLASS[a.statusId] || "wdChipFilo")}>{a.status}</span>{a.escalated && a.statusId !== 4 ? <span className="wdChip wdChipOro">Escalado</span> : null}</div>
						<div className="auMotivo"><b>{a.reasonName}</b>{a.student ? <small>{a.student.name}{a.student.identityCode ? " · " + a.student.identityCode : ""}</small> : null}</div>
						<div className="auDetalle">{a.details}</div>
						<div className="wdTenue" style={{ fontSize: 12 }}>{fechaHora(a.createdAt)}{a.receiptId ? " · pago " + a.receiptId : ""}<br />{a.createdBy}</div>
					</button>
					{abierto && abierto.id === a.id ? detalle(abierto) : null}
				</React.Fragment>
			))}
		</div>
	);
};

export default AuditsController;
